import { useCallback } from 'react';
import Resizer from 'react-image-file-resizer';
import { toast } from 'react-toastify';
import { findFieldByNameAnywhere, setAtPath } from '../../FormBuilder/helpers/fieldTree';

// Immutable "find this field by name, wherever it lives (top-level, or one
// level inside a Container's own `.controls`), and replace it" — every
// handler below used to do a flat `prev.map(item => item.name === name ? ... : item)`,
// which a Container's own `type` never matches and which never descends
// into `.controls`, so a media/file field living inside a Container never
// got its upload/select/clear applied to form state at all (the preview
// might show locally, but the actual `formArray` — and so the save payload —
// never saw it). `updater` receives the unmatched-type case too (mirroring
// every call site's own `item.type === 'X' ? {...} : item` guard), so
// passing it straight through preserves each handler's existing semantics.
const updateFieldByName = (prev, name, updater) => {
  const found = findFieldByNameAnywhere(prev, name);
  if (!found) return prev;
  return setAtPath(prev, found.path, updater);
};

const ALLOWED_MIME_TYPES = [
  'image/jpeg', 'image/png', 'image/gif',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-powerpoint',
];
const ALLOWED_EXTENSIONS = ['xlsx', 'xls', 'csv', 'doc', 'docx', 'ppt', 'pptx', 'pdf', 'jpeg', 'jpg', 'png', 'gif'];
const MAX_IMAGE_SIZE_BYTES = 300 * 1024;
const MAX_FILE_SIZE_MB     = 3;

/**
 * useMediaHandlers
 *
 * Encapsulates image, signature, and file-upload handlers previously
 * inlined in SubmissionDynamicform.
 *
 * @param {Object} params
 * @param {React.MutableRefObject} params.formArrayRef  - ref to latest formArray
 * @param {Function} params.setArray                    - formArray state setter
 * @param {Function} params.setSelectedImages           - image state setter
 * @param {Function} params.setSelectedSignature        - signature state setter
 * @param {Function} params.setSelectedAudio            - audio state setter
 * @param {Function} params.setSelectedPhoto            - photo capture state setter
 * @param {Function} params.setIsFormEdited             - form-dirty flag setter
 * @param {Function} params.clearErrors                 - RHF clearErrors
 * @param {Function} params.trigger                     - RHF trigger
 * @param {string}   params.baseURL                     - image CDN base URL
 * @param {string}   params.SignURL                     - signature CDN base URL
 * @param {boolean}  params.isEditMode                  - true when editing an existing record
 * @param {Object}   params.dataUploadAuthAxios          - axios instance for file uploads
 * @param {Function} params.setIsGlobalLoading           - global spinner setter
 */
export function useMediaHandlers({
  formArrayRef,
  setArray,
  setSelectedImages,
  setSelectedSignature,
  setSelectedAudio,
  setSelectedPhoto,
  setIsFormEdited,
  clearErrors,
  trigger,
  baseURL,
  SignURL,
  isEditMode,
  dataUploadAuthAxios,
  setIsGlobalLoading,
}) {

  // ─── Helpers ──────────────────────────────────────────────────────────────

  const resizeImage = useCallback((file) =>
    new Promise((resolve) => {
      Resizer.imageFileResizer(file, 800, 800, 'JPEG', 90, 0, (uri) => resolve(uri));
    }), []
  );

  const validateFile = useCallback((file) => {
    if (file.size / 1024 / 1024 > MAX_FILE_SIZE_MB) {
      return `File size must be less than ${MAX_FILE_SIZE_MB}MB`;
    }
    const ext = file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_MIME_TYPES.includes(file.type) && !ALLOWED_EXTENSIONS.includes(ext)) {
      return 'Invalid file type';
    }
    return true;
  }, []);

  // ─── Image handlers ───────────────────────────────────────────────────────

  const onclickImage = useCallback((name) => {
    setSelectedImages((prev) => ({ ...prev, [name]: undefined }));
    setArray((prev) =>
      updateFieldByName(prev, name, (item) =>
        item.type === 'Image' ? { ...item, value: '' } : item
      )
    );
  }, [setSelectedImages, setArray]);

  const handleImageSelect = useCallback(async (event, fieldName) => {
    const file = event.target.files[0];
    clearErrors(fieldName);
    setIsFormEdited(true);
    if (!file) return;

    const processBase64 = (dataUri) => {
      setSelectedImages((prev) => ({ ...prev, [fieldName]: dataUri }));
      setArray((prev) =>
        updateFieldByName(prev, fieldName, (item) =>
          item.type === 'Image'
            ? { ...item, value: { base64: dataUri.split(',')[1] } }
            : item
        )
      );
    };

    if (file.size <= MAX_IMAGE_SIZE_BYTES) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => processBase64(reader.result);
    } else {
      try {
        const compressed = await resizeImage(file);
        processBase64(compressed);
      } catch {
        toast.error('Error compressing image. Please try again or use a smaller file.');
      }
    }
  }, [clearErrors, setIsFormEdited, setSelectedImages, setArray, resizeImage]);

  // handleImageClick: kept in the parent component (needs setSelectedImage state)

  // ─── Multi-image handlers ─────────────────────────────────────────────────

  const handleMultiImageSelect = useCallback(async (event, fieldName) => {
    const files = event.target.files;
    if (!files.length) return;

    clearErrors(`${fieldName}_required_guard`);
    setIsFormEdited(true);

    const newImages   = [];
    const base64Array = [];

    for (let i = 0; i < files.length; i++) {
      const reader = new FileReader();
      await new Promise((resolve) => {
        reader.onload = () => {
          newImages.push(reader.result);
          base64Array.push({ base64: reader.result.split(',')[1] });
          resolve();
        };
        reader.readAsDataURL(files[i]);
      });
    }

    setSelectedImages((prev) => ({
      ...prev,
      [fieldName]: [...(prev[fieldName] || []), ...newImages],
    }));

    setArray((prev) =>
      updateFieldByName(prev, fieldName, (item) => {
        if (item.type !== 'MultiImage' && item.type !== 'CropHealthAnalyzer') return item;
        return { ...item, value: [...(item.value || []), ...base64Array] };
      })
    );

    event.target.value = '';
  }, [clearErrors, setIsFormEdited, setSelectedImages, setArray]);

  // CropHealthAnalyzer is a single-image field, but its value is kept as a
  // 1-item array (`[{ base64 }]`) so the existing backend upload/analysis
  // loop (which expects `el.value` to be an array) keeps working unchanged.
  const handleCropImageSelect = useCallback(async (event, fieldName) => {
    const file = event.target.files[0];
    clearErrors(`${fieldName}_required_guard`);
    setIsFormEdited(true);
    if (!file) return;

    const processBase64 = (dataUri) => {
      setSelectedImages((prev) => ({ ...prev, [fieldName]: dataUri }));
      setArray((prev) =>
        updateFieldByName(prev, fieldName, (item) =>
          item.type === 'CropHealthAnalyzer'
            ? { ...item, value: [{ base64: dataUri.split(',')[1] }] }
            : item
        )
      );
    };

    if (file.size <= MAX_IMAGE_SIZE_BYTES) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => processBase64(reader.result);
    } else {
      try {
        const compressed = await resizeImage(file);
        processBase64(compressed);
      } catch {
        toast.error('Error compressing image. Please try again or use a smaller file.');
      }
    }

    event.target.value = '';
  }, [clearErrors, setIsFormEdited, setSelectedImages, setArray, resizeImage]);

  const handleClearCropImage = useCallback((fieldName) => {
    setIsFormEdited(true);
    setSelectedImages((prev) => ({ ...prev, [fieldName]: undefined }));
    setArray((prev) =>
      updateFieldByName(prev, fieldName, (item) =>
        item.type === 'CropHealthAnalyzer'
          ? { ...item, value: undefined }
          : item
      )
    );
    trigger(`${fieldName}_required_guard`);
  }, [setIsFormEdited, setSelectedImages, setArray, trigger]);

  const handleDeleteMultiImage = useCallback((fieldName, index) => {
    setIsFormEdited(true);

    setSelectedImages((prev) => {
      const updated = [...(prev[fieldName] || [])];
      updated.splice(index, 1);
      return { ...prev, [fieldName]: updated.length > 0 ? updated : undefined };
    });

    setArray((prev) =>
      updateFieldByName(prev, fieldName, (item) => {
        const valueArray = [...(item.value || [])];
        valueArray.splice(index, 1);
        return { ...item, value: valueArray.length > 0 ? valueArray : undefined };
      })
    );

    trigger(`${fieldName}_required_guard`);
  }, [setIsFormEdited, setSelectedImages, setArray, trigger]);

  // ─── Signature handlers ───────────────────────────────────────────────────

  const onClickSignature = useCallback((name) => {
    setSelectedSignature((prev) => ({ ...prev, [name]: undefined }));
    setArray((prev) =>
      updateFieldByName(prev, name, (item) =>
        item.type === 'Signature' ? { ...item, value: '' } : item
      )
    );
  }, [setSelectedSignature, setArray]);

  const handleSignatureSelect = useCallback(async (event, fieldName) => {
    clearErrors(fieldName);
    const file = event.target.files[0];
    if (!file) return;

    const processBase64 = (dataUri) => {
      setSelectedSignature((prev) => ({ ...prev, [fieldName]: dataUri }));
      setArray((prev) =>
        updateFieldByName(prev, fieldName, (item) =>
          item.type === 'Signature'
            ? { ...item, value: { base64: dataUri.split(',')[1] } }
            : item
        )
      );
    };

    if (file.size <= MAX_IMAGE_SIZE_BYTES) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => processBase64(reader.result);
    } else {
      try {
        const compressed = await resizeImage(file);
        processBase64(compressed);
      } catch {
        toast.error('Error compressing signature image. Please try again or use a smaller file.');
      }
    }
  }, [clearErrors, setSelectedSignature, setArray, resizeImage]);

  // ─── Audio capture handlers ───────────────────────────────────────────────

  // dataUri comes from AudioCapture.jsx's own MediaRecorder capture (already
  // a complete audio Blob read via FileReader) — no resize/compression step,
  // audio can't be downsized the way images can.
  const handleAudioCapture = useCallback((dataUri, mimeType, fieldName) => {
    clearErrors(`${fieldName}_required_guard`);
    setIsFormEdited(true);
    setSelectedAudio((prev) => ({ ...prev, [fieldName]: dataUri }));
    setArray((prev) =>
      updateFieldByName(prev, fieldName, (item) =>
        item.type === 'AudioCapture'
          ? { ...item, value: { base64: dataUri.split(',')[1], mimeType } }
          : item
      )
    );
  }, [clearErrors, setIsFormEdited, setSelectedAudio, setArray]);

  const handleClearAudio = useCallback((fieldName) => {
    setIsFormEdited(true);
    setSelectedAudio((prev) => ({ ...prev, [fieldName]: undefined }));
    setArray((prev) =>
      updateFieldByName(prev, fieldName, (item) =>
        item.type === 'AudioCapture'
          ? { ...item, value: undefined }
          : item
      )
    );
    trigger(`${fieldName}_required_guard`);
  }, [setIsFormEdited, setSelectedAudio, setArray, trigger]);

  // ─── Photo capture handlers ────────────────────────────────────────────────

  // dataUri comes from PhotoCapture.jsx's own canvas.toDataURL capture; geo is
  // { latitude, longitude, timestamp } captured at the same moment (or nulls
  // if geolocation was denied/unavailable) — bundled into one value object
  // alongside the image, same as AudioCapture bundles base64+mimeType.
  const handlePhotoCapture = useCallback((dataUri, geo, fieldName) => {
    clearErrors(`${fieldName}_required_guard`);
    setIsFormEdited(true);
    setSelectedPhoto((prev) => ({ ...prev, [fieldName]: dataUri }));
    setArray((prev) =>
      prev.map((item) =>
        item.name === fieldName && item.type === 'PhotoCapture'
          ? { ...item, value: { base64: dataUri.split(',')[1], mimeType: 'image/jpeg', ...geo } }
          : item
      )
    );
  }, [clearErrors, setIsFormEdited, setSelectedPhoto, setArray]);

  const handleClearPhoto = useCallback((fieldName) => {
    setIsFormEdited(true);
    setSelectedPhoto((prev) => ({ ...prev, [fieldName]: undefined }));
    setArray((prev) =>
      prev.map((item) =>
        item.name === fieldName && item.type === 'PhotoCapture'
          ? { ...item, value: undefined }
          : item
      )
    );
    trigger(`${fieldName}_required_guard`);
  }, [setIsFormEdited, setSelectedPhoto, setArray, trigger]);

  // ─── File upload handlers ─────────────────────────────────────────────────

  // clearFile is defined before handleFileChange so it can be in the dep array.
  const clearFile = useCallback((name) => {
    setArray((prev) =>
      updateFieldByName(prev, name, (item) => ({ ...item, fileName: '', value: '' }))
    );
  }, [setArray]);

  const handleFileChange = useCallback(async (event, name) => {
    const file = event.target.files[0];
    if (!file) return;

    const validationResult = validateFile(file);
    if (validationResult !== true) {
      toast.error(validationResult, { autoClose: 3000 });
      return;
    }

    clearErrors(name);
    setIsGlobalLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await dataUploadAuthAxios.post(
        'api/v1/formSubmission/fileUploadS3',
        formData
      );
      if (response.status === 200) {
        setArray((prev) =>
          updateFieldByName(prev, name, (item) =>
            ({ ...item, fileName: file.name, value: response.data?.fileKey })
          )
        );
        toast.success('File uploaded successfully!', { autoClose: 1000 });
      }
    } catch (error) {
      clearFile(name);
      toast.error(
        error?.response?.data?.message ||
        'Apologies for the inconvenience. We are currently addressing an unexpected error.',
        { autoClose: 5000 }
      );
    } finally {
      setIsGlobalLoading(false);
    }
  }, [validateFile, clearErrors, setIsGlobalLoading, dataUploadAuthAxios, setArray, clearFile]);

  const handleClick = useCallback((name) => {
    document.getElementById(name)?.click();
  }, []);

  // ─── Public API ───────────────────────────────────────────────────────────

  return {
    onclickImage,
    handleImageSelect,
    handleMultiImageSelect,
    handleDeleteMultiImage,
    handleCropImageSelect,
    handleClearCropImage,
    onClickSignature,
    handleSignatureSelect,
    handleAudioCapture,
    handleClearAudio,
    handlePhotoCapture,
    handleClearPhoto,
    handleFileChange,
    clearFile,
    handleClick,
    validateFile,
  };
}
